/**
 * Cloud arc-event + temp-cycle log APIs.
 * GET/DELETE /api/arc-event-logs/ · GET/DELETE /api/temp-cycle-logs/
 */

import { CLOUD_API_BASE } from "./cloudApi";

const ARC_URL = `${CLOUD_API_BASE}/api/arc-event-logs/`;
const TEMP_URL = `${CLOUD_API_BASE}/api/temp-cycle-logs/`;

const asList = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.logs)) return data.logs;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const readError = async (res) => {
  const data = await res.json().catch(() => ({}));
  const msg = data.detail || data.error || data.message;
  if (Array.isArray(msg)) return msg.join(" ");
  if (typeof msg === "string" && msg.trim()) return msg;
  return `Request failed (${res.status})`;
};

/** Normalize arc/temp log rows so existing UI fields still work. */
export const normalizeLogRow = (row, fallbackType) => {
  if (!row || typeof row !== "object") return row;
  const id = row.id ?? row.pk ?? null;
  const hasTempCycle =
    Array.isArray(row.samples) ||
    Array.isArray(row.temperatures) ||
    row.peak_temp != null ||
    row.sample_count != null;
  const eventType =
    row.event_type ||
    row.eventType ||
    fallbackType ||
    (hasTempCycle || row.temperature != null ? "temperature" : "arc");
  return {
    ...row,
    id,
    sensor_id: row.sensor_id ?? row.sensorId ?? row.sensor,
    serial_number: row.serial_number || row.serial || "",
    date: row.date || (row.created_at || row.started_at || row.timestamp || "").toString().slice(0, 10),
    time:
      row.time ||
      (() => {
        const raw = String(row.created_at || row.started_at || row.timestamp || "");
        const m = raw.match(/(\d{1,2}:\d{2}(?::\d{2})?)/);
        return m ? m[1] : "";
      })(),
    event_type: eventType,
    arc_level: row.arc_level ?? row.level ?? row.severity ?? 0,
    threshold: row.threshold ?? row.ir_alarm_set_temp ?? row.threshold_value,
    temperature:
      row.temperature ??
      row.peak_temp ??
      row.temp ??
      row.ir_temperature ??
      (Array.isArray(row.temperatures) ? Math.max(...row.temperatures.map(Number).filter(Number.isFinite)) : null),
  };
};

export const fetchArcEventLogs = async () => {
  const res = await fetch(ARC_URL, { headers: { Accept: "application/json" } });
  if (!res.ok) throw new Error(await readError(res));
  return asList(await res.json()).map((row) => normalizeLogRow(row, "arc"));
};

export const fetchTempCycleLogs = async () => {
  const res = await fetch(TEMP_URL, { headers: { Accept: "application/json" } });
  if (!res.ok) throw new Error(await readError(res));
  return asList(await res.json()).map((row) => normalizeLogRow(row, "temperature"));
};

/** Combined newest-first list (arc + temp) for Logs page / dashboard panels. */
export const fetchAllEventLogs = async () => {
  const [arcs, temps] = await Promise.all([
    fetchArcEventLogs().catch(() => []),
    fetchTempCycleLogs().catch(() => []),
  ]);
  const stamp = (row) =>
    `${row.date || ""} ${row.time || ""} ${row.id || ""}`.trim();
  return [...arcs, ...temps].sort((a, b) => String(stamp(b)).localeCompare(String(stamp(a))));
};

export const deleteArcEventLog = async (id) => {
  if (id == null) throw new Error("Log id is required");
  const res = await fetch(`${ARC_URL}${encodeURIComponent(id)}/`, { method: "DELETE" });
  if (!res.ok && res.status !== 204) throw new Error(await readError(res));
  return true;
};

export const deleteTempCycleLog = async (id) => {
  if (id == null) throw new Error("Log id is required");
  const res = await fetch(`${TEMP_URL}${encodeURIComponent(id)}/`, { method: "DELETE" });
  if (!res.ok && res.status !== 204) throw new Error(await readError(res));
  return true;
};

export const deleteArcEventLogsBulk = async (ids = []) => {
  const res = await fetch(ARC_URL, {
    method: "DELETE",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ ids }),
  });
  if (!res.ok && res.status !== 204) throw new Error(await readError(res));
  return true;
};

export const deleteTempCycleLogsBulk = async (ids = []) => {
  const res = await fetch(TEMP_URL, {
    method: "DELETE",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ ids }),
  });
  if (!res.ok && res.status !== 204) throw new Error(await readError(res));
  return true;
};

export const isTempLog = (item) =>
  String(item?.event_type || "").toLowerCase() === "temperature";

export const deleteEventLog = async (item) => {
  const id = item?.id;
  if (id == null) throw new Error("Log id is required for cloud delete");
  if (isTempLog(item)) return deleteTempCycleLog(id);
  return deleteArcEventLog(id);
};

export const fetchTempCycleLogById = async (id) => {
  if (id == null) throw new Error("Temp cycle id is required");
  const res = await fetch(`${TEMP_URL}${encodeURIComponent(id)}/`, {
    headers: { Accept: "application/json" },
  });
  if (!res.ok) throw new Error(await readError(res));
  return normalizeLogRow(await res.json(), "temperature");
};

/**
 * Cloud temp-cycle `samples` / `temperatures` are number arrays, e.g. [175, 182, 59].
 * Older shape used objects: [{ t, temp }, ...].
 */
const toChartSamples = (row) => {
  const raw = Array.isArray(row?.samples)
    ? row.samples
    : Array.isArray(row?.temperatures)
    ? row.temperatures
    : Array.isArray(row?.cycle)
    ? row.cycle
    : Array.isArray(row?.data_points)
    ? row.data_points
    : null;

  if (!raw || !raw.length) {
    const peak = Number(row?.peak_temp ?? row?.temperature);
    if (Number.isFinite(peak)) {
      return [{ t: row?.time || "#1", temp: peak }];
    }
    return [];
  }

  return raw
    .map((s, i) => {
      if (s != null && typeof s === "object") {
        const temp = Number(s.temp ?? s.temperature ?? s.value);
        return {
          t: s.t ?? s.time ?? s.timestamp ?? `#${i + 1}`,
          temp: Number.isFinite(temp) ? temp : null,
        };
      }
      const temp = Number(s);
      return {
        t: `#${i + 1}`,
        temp: Number.isFinite(temp) ? temp : null,
      };
    })
    .filter((s) => s.temp != null);
};

/**
 * Load cycle graph data for Details modal.
 * Prefers id; otherwise finds matching row from the list.
 * Returns a shape compatible with TempCycleGraphModal ({ found, samples, threshold, status }).
 */
export const fetchTempCycleForEvent = async (meta) => {
  let row = null;
  if (meta?.id != null) {
    row = await fetchTempCycleLogById(meta.id);
  } else {
    const list = await fetchTempCycleLogs();
    row =
      list.find(
        (item) =>
          String(item.sensor_id) === String(meta?.sensor_id) &&
          String(item.date || "") === String(meta?.date || "") &&
          String(item.time || "").startsWith(String(meta?.time || "").slice(0, 5))
      ) || null;
  }

  if (!row) {
    return { found: false, samples: [], threshold: meta?.threshold ?? null, status: "missing" };
  }

  const samples = toChartSamples(row);
  const statusRaw = String(row.status || "").toLowerCase();
  const status =
    statusRaw === "active" || statusRaw === "running" || statusRaw === "open"
      ? "active"
      : samples.length
      ? "closed"
      : "missing";

  return {
    found: samples.length > 0,
    id: row.id,
    sensor_id: row.sensor_id,
    date: row.date,
    time: row.time,
    threshold: row.threshold ?? meta?.threshold ?? null,
    peak_temp: row.peak_temp ?? row.temperature ?? null,
    status,
    samples,
  };
};
